"use client";

import { Button, Heading, ProductCard } from "@/components/atoms";
import SkeletonPC from "@/components/atoms/ProductCard/SkeletonPC";
import ProductGrid from "@/components/molecules/ProductGrid";
import StoryblokComponent from "@/lib/storyblok/storyblokSetup/StoryblokComponent/StoryblokComponent";

import useIntersection from "@/lib/hooks/useIntersection";
import useScrollToTop from "@/lib/hooks/useScrollToTop";
import type {
  ContentBlock,
  GetListingResponse,
} from "@depict-ai/types/api/GetListingResponse";
import { Fragment, memo, useMemo } from "react";

import { HlsPlayer } from "@/components/atoms/HLSPlayer";
import { ProductCard as PC } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { ProductListingPageStoryblok } from "@/types/storyblok-blok-types";
import { useMediaQuery } from "@frend-digital/ui/hooks";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import styles from "./products-section.module.css";

const ProductsSection = ({
  data: { plpContent, listingPage, fetchNextPage, hasNextPage },
}: {
  data: {
    plpContent: (
      | {
          products: PC[];
          editorial?: NonNullable<ProductListingPageStoryblok["bloks"]>[number];
        }
      | undefined
    )[];
    listingPage: GetListingResponse | null;
    fetchNextPage: () => Promise<void>;

    hasNextPage: boolean;
  };
}) => {
  const scrollToTop = useScrollToTop();
  const callbackFunction = (
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage) {
      fetchNextPage();
    }
  };
  const { Ref } = useIntersection(callbackFunction);

  const sumProductsVisible = useMemo(() => {
    return plpContent?.reduce(
      (prev, curr) => prev + (curr?.products?.length || 0),
      0
    );
  }, [plpContent]);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "6rem" }}>
      <ProductGrid id="grid">
        {plpContent?.map((section, i) => (
          <Fragment key={`section-${i}`}>
            {section?.products?.map((product, i) => {
              return (
                <ProductCard
                  className="product-card"
                  key={`product-${product?.core?.slug}-${i}`}
                  product={product}
                />
              );
            })}

            {section?.editorial && (
              <div
                key={"article__" + i}
                className={styles["editorial-wrapper"]}
              >
                <StoryblokComponent blok={section?.editorial} preview={true} />
              </div>
            )}
          </Fragment>
        ))}

        <ContentBlocks
          content_blocks={listingPage?.content_blocks || []}
          total={sumProductsVisible}
        />

        {hasNextPage && (
          <>
            <div id="observer" ref={Ref}>
              <SkeletonPC />
            </div>
            <SkeletonPC /> <SkeletonPC /> <SkeletonPC />
          </>
        )}
      </ProductGrid>

      {!hasNextPage && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            gap: "1.5rem",
          }}
        >
          <Heading as="span" styledAs="h4">
            There are no more products to show
          </Heading>
          <Button outlined onClick={scrollToTop}>
            Back to top
          </Button>
        </div>
      )}
    </section>
  );
};

function getDepictColumn(block: ContentBlock) {
  if (block.span_columns === 4) {
    return `1 / span 4`;
  }
  switch (block.position) {
    case "left":
      return `1 / span ${block.span_columns}`;
    case "right":
      return `span ${block.span_columns} / -1`;
    default:
      return `1 / span ${block.span_columns}`;
  }
}

function getDepictLink(block: ContentBlock) {
  switch (typeof block.content.link) {
    case "string":
      return block.content.link;
    default:
      return undefined;
  }
}

const ContentBlocks = memo(
  ({
    content_blocks,
    total,
  }: {
    content_blocks: ContentBlock[];
    total: number;
  }) => {
    const isLargeScreen = useMediaQuery("(min-width: 1024px)");

    const contentBlocks = useMemo(() => {
      // Sort them by row number
      const sorted = content_blocks.toSorted((a, b) => {
        return a.row - b.row;
      });

      const offset = sorted.map((block, index, all) => {
        if (!isLargeScreen) {
          // We must offset the row number by the number of blocks that currently have been rendered
          const previouslyRenderedBlocks = all.slice(0, index).length;
          block.row = block.row + previouslyRenderedBlocks;
        }

        return { ...block };
      });

      const components = offset.map((block, index, all) => {
        // The grid columns are set to 4, which means that we must calculate based on the number visible products if we want to show a column block.
        // Example: total products 29, that means we have 8 rows, but we have 9 content blocks, we must make sure we have enough space

        const colNumber = isLargeScreen ? 4 : 2;

        const totalRows = Math.ceil(total / colNumber);
        const rowOffset = !isLargeScreen ? 2 * all.length : 0;

        if (block.row > totalRows + rowOffset) {
          // We don't have enough products to render enough rows to show this content block
          return null;
        }

        return <ContentBlock block={block} key={index} />;
      });

      return components;
    }, [isLargeScreen, content_blocks, total]);

    return <>{contentBlocks}</>;
  }
);

ContentBlocks.displayName = "ContentBlocks";

const ContentBlock = ({ block }: { block: ContentBlock }) => {
  const row = block.row + 1;

  const link = getDepictLink(block);

  const Component = link ? Link : "article";

  const isFirstColWithRowSpan = block.span_rows === 1;
  const isOnFirstRow = block.row === 0 && block.span_rows === 1;

  return (
    <Component
      href={link!}
      className={clsx(
        styles.contentBlock,
        isFirstColWithRowSpan && styles.isFirstColWithRowSpan,
        isOnFirstRow && styles.isOnFirstRow
      )}
      data-span-all={block.span_columns === 4 ? true : undefined}
      style={
        {
          position: "relative",
          "--col-span": block.span_columns,
          "--row-span": block.span_rows,
          "--row": row,
          "--grid-column": getDepictColumn(block),
        } as React.CSSProperties
      }
    >
      <div className={styles.contentBlockWrap}>
        <ContentBlockMedia block={block} />
      </div>
    </Component>
  );
};

const ContentBlockMedia = ({ block }: { block: ContentBlock }) => {
  if (block.content.type === "video") {
    return (
      <HlsPlayer
        loop
        className={styles.video}
        autoPlay
        muted
        src={block.content.url}
      />
    );
  }
  return (
    <Image
      priority={block.row < 4}
      fill
      style={{
        backgroundColor: "var(--color-primary-10)",
        objectFit: "cover",
      }}
      src={block.content.url}
      alt={block.content.url}
    />
  );
};

export default ProductsSection;
